import { useCallback, useMemo, useRef, useState } from "react";

import { createNoteUpdate, TEXT_LENGTH_LIMIT, TITLE_LENGTH_LIMIT } from "@fokused-me/core";
import {
   ContentModeType,
   ContentPanelActionType,
   FMAppContentPanel,
   FMConfirmDialog,
   FMPanelEdit,
   FMSnackbars,
   IPatch,
} from "@fokused-me/web-ui";
import { List, Set } from "immutable";
import { IEditPanelInfo } from "../../../state/Model";
import { createPanelInfo, useDesktop } from "../../../state/Desktop";
import { IContentPanelProps } from "./ContentPanelSelector";
import { newNotePanel, useBreadcrumbs } from "./Helpers";
import { Button } from "@material-ui/core";

import { diff_match_patch } from "diff-match-patch";
const DMP = new diff_match_patch();

const serialisePatches = (patches: List<IPatch[]>) =>
   patches ? patches.map((it) => DMP.patch_toText(it as any)) : List();

const deserialisePatches = (patches: List<string>) => (patches ? patches.map((it) => DMP.patch_fromText(it)) : List());

export interface IEditContentPanelProps extends IContentPanelProps {
   panelInfo: IEditPanelInfo;
}

export const EditContentPanel = ({ notepad, panelInfo }: IEditContentPanelProps) => {
   const { closePanels, setPanelType, addPanel, toggleExpandPanel, setEditPatches } = useDesktop();
   const { setSnackbar } = FMSnackbars.useContainer();

   const [openSaveWarningDialog, setOpenSaveWarningDialog] = useState(false);

   const note = useMemo(() => notepad?.getNote(panelInfo.rootNoteId), [notepad, panelInfo.rootNoteId]);

   const effectivePatches = useMemo(() => deserialisePatches(panelInfo.effectivePatches), [panelInfo.effectivePatches]);

   const [isReady, setIsReady] = useState(!!effectivePatches.size);

   const breadcrumbs = useBreadcrumbs(panelInfo, notepad, true);

   const handleModeChange = useCallback(
      (mode: ContentModeType) => setPanelType(panelInfo.id, mode),
      [panelInfo.id, setPanelType]
   );

   const handleViewClick = useCallback(
      (rootNoteId: string) => () => {
         const panel = createPanelInfo({
            notepadId: panelInfo.notepadId,
            rootNoteId,
            type: "note",
         });
         addPanel(panel);
      },
      [panelInfo.notepadId, addPanel]
   );

   const contentTextRef = useRef("");
   const handlePanelAction = useCallback(
      (action: ContentPanelActionType) => {
         switch (action) {
            case "expand":
               toggleExpandPanel(panelInfo.id);
               break;
            case "close":
               if (panelInfo.effectivePatches.size) {
                  setOpenSaveWarningDialog(true);
               } else {
                  closePanels(List([panelInfo.id]));
               }
               break;
            case "undo":
               setEditPatches(panelInfo.id, panelInfo.patches, panelInfo.effectivePatches.pop());
               break;
            case "redo":
               setEditPatches(
                  panelInfo.id,
                  panelInfo.patches,
                  panelInfo.patches.slice(0, panelInfo.effectivePatches.size + 1)
               );
               break;
            case "save":
               const [title, text] = splitContent(contentTextRef.current);
               const update = panelInfo.isNewNote
                  ? createNoteUpdate({
                       parentId: panelInfo.rootNoteId,
                       title,
                       text,
                    })
                  : createNoteUpdate({
                       noteId: panelInfo.rootNoteId,
                       title,
                       text,
                    });
               notepad?.apply(List([update]));
               closePanels(List([panelInfo.id]));
               setSnackbar({
                  message: "Note has been saved",
                  severity: "success",
                  action: (
                     <Button color="inherit" size="small" onClick={handleViewClick(update.noteId)}>
                        View
                     </Button>
                  ),
               });
               break;
            case "add":
               newNotePanel(panelInfo, addPanel);
               break;
         }
      },
      [notepad, panelInfo, closePanels, addPanel, setSnackbar, handleViewClick, toggleExpandPanel, setEditPatches]
   );

   const actions = useMemo(() => Set.of<ContentPanelActionType>("add", "save", "undo", "redo"), []);

   const disableActions = useMemo(() => {
      let result = Set<ContentPanelActionType>();
      if (!panelInfo.effectivePatches.size) {
         result = result.add("undo");
      }

      if (panelInfo.patches.size === panelInfo.effectivePatches.size) {
         result = result.add("redo");
      }

      if (!isReady || !panelInfo.effectivePatches.size) {
         result = result.add("save");
      }

      if (panelInfo.isNewNote) {
         result = result.add("add");
      }

      return result;
   }, [isReady, panelInfo.patches, panelInfo.effectivePatches, panelInfo.isNewNote]);

   const handlePatchesChange = useCallback(
      (newPatches: List<IPatch[]>) => {
         const serNewPatches = serialisePatches(newPatches);
         setEditPatches(panelInfo.id, serNewPatches, serNewPatches);
      },
      [panelInfo.id, setEditPatches]
   );

   const handleTextChange = useCallback((newText: string) => {
      contentTextRef.current = newText;
   }, []);

   const handleSaveWarningResult = useCallback(
      (result: number) => {
         switch (result) {
            case 0: // save
               handlePanelAction("save");
               break;
            case 1: // close
               closePanels(List(panelInfo.id));
               break;
            case 2: // back
               break;
         }
         setOpenSaveWarningDialog(false);
      },
      [panelInfo.id, closePanels, handlePanelAction]
   );

   const handleKeyUp = useCallback(
      (event: KeyboardEvent) => {
         if (!isReady) {
            return;
         }

         if (event.key === "Escape") {
            handlePanelAction("close");
         } else if (event.key === "Enter" && event.ctrlKey) {
            handlePanelAction("save");
         }
      },
      [isReady, handlePanelAction]
   );

   return (
      <FMAppContentPanel
         expanded={panelInfo.expanded}
         actions={actions}
         disableActions={disableActions}
         disableModes={Set<ContentModeType>().add("bin")}
         highlightedActions={Set()}
         mode={panelInfo.type}
         breadcrumbs={breadcrumbs}
         onAction={handlePanelAction}
         onModeChange={handleModeChange}
         onBreadcrumbsNavigate={console.log}
         onSearchTextChange={console.log}
      >
         <FMPanelEdit
            initialValue={note && !panelInfo.isNewNote ? `# ${note.title || ""}\n${note.text || ""}` : "# "}
            onChange={handleTextChange}
            patches={effectivePatches}
            onPatchesChange={handlePatchesChange}
            onReadyStatusChange={setIsReady}
            onKeyUp={handleKeyUp}
         />
         <FMConfirmDialog
            open={openSaveWarningDialog}
            title="You have unsaved changes!"
            text="Do you want to save your changes before closing this panel, close without saving or go back to editing?"
            buttons={List.of("Save", "Close", "Back")}
            onResult={handleSaveWarningResult}
         />
      </FMAppContentPanel>
   );
};

function splitContent(content: string): [string, string] {
   let newTitle = "";
   let newText = "";
   const lineEndPos = content.indexOf("\n");
   if (lineEndPos >= 0) {
      newTitle = content.substr(0, lineEndPos).replace("#", "").trim();
      newText = content.substr(lineEndPos + 1);
   } else {
      newTitle = content.substr(0, TITLE_LENGTH_LIMIT).replace("#", "").trim();
      newText = content.substr(TITLE_LENGTH_LIMIT + 1);
   }

   if (newTitle.length > TITLE_LENGTH_LIMIT) {
      newTitle = newTitle.substr(TITLE_LENGTH_LIMIT);
      newText = content;
   }

   if (newText.length > TEXT_LENGTH_LIMIT) {
      newText = newText.substr(TEXT_LENGTH_LIMIT);
   }

   return [newTitle, newText];
}
